import { useEffect } from 'react';

interface SEOProps {
    title?: string;
    description?: string;
    keywords?: string;
    canonicalUrl?: string;
    ogType?: 'website' | 'article' | 'profile';
    ogImage?: string;
    ogImageAlt?: string;
    twitterCard?: 'summary' | 'summary_large_image';
    noIndex?: boolean;
    language?: 'de' | 'en';
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    structuredData?: object | object[];
}

const BASE_URL = 'https://koricode.de';
const DEFAULT_TITLE = 'KoriCode | Maßgeschneiderte Software-Entwicklung';
const DEFAULT_DESCRIPTION =
    'Professionelle Fullstack-Entwicklung für Web-Apps, Mobile Apps und Cloud-Lösungen. Von der Idee bis zur Live-Schaltung – Ihr IT-Partner in München.';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

/**
 * SEO Component für dynamische Meta-Tags
 * Aktualisiert document.head zur Laufzeit
 */
export function SEO({
    title,
    description = DEFAULT_DESCRIPTION,
    keywords,
    canonicalUrl,
    ogType = 'website',
    ogImage = DEFAULT_IMAGE,
    ogImageAlt = 'KoriCode Software-Entwicklung',
    twitterCard = 'summary_large_image',
    noIndex = false,
    language = 'de',
    publishedTime,
    modifiedTime,
    author = 'KoriCode',
    section,
    structuredData,
}: SEOProps) {
    const fullTitle = title ? `${title} | KoriCode` : DEFAULT_TITLE;
    const fullUrl = canonicalUrl ? `${BASE_URL}${canonicalUrl}` : BASE_URL;
    const locale = language === 'de' ? 'de_DE' : 'en_US';

    useEffect(() => {
        // Update document title
        document.title = fullTitle;

        // Helper function to update or create meta tag
        const updateMeta = (
            selector: string,
            attribute: string,
            value: string,
            createName?: string
        ) => {
            let element = document.querySelector(selector) as HTMLMetaElement;
            if (!element && createName) {
                element = document.createElement('meta');
                if (selector.includes('property=')) {
                    element.setAttribute(
                        'property',
                        createName
                    );
                } else {
                    element.setAttribute('name', createName);
                }
                document.head.appendChild(element);
            }
            if (element) {
                element.setAttribute(attribute, value);
            }
        };

        // Helper function to update or create link tag
        const updateLink = (
            rel: string,
            href: string,
            additionalAttrs?: Record<string, string>
        ) => {
            let selector = `link[rel="${rel}"]`;
            if (additionalAttrs?.hreflang) {
                selector = `link[rel="${rel}"][hreflang="${additionalAttrs.hreflang}"]`;
            }
            let element = document.querySelector(selector) as HTMLLinkElement;
            if (!element) {
                element = document.createElement('link');
                element.rel = rel;
                if (additionalAttrs) {
                    Object.entries(additionalAttrs).forEach(([key, val]) => {
                        element.setAttribute(key, val);
                    });
                }
                document.head.appendChild(element);
            }
            element.href = href;
        };

        // Primary Meta Tags
        updateMeta('meta[name="description"]', 'content', description, 'description');
        updateMeta('meta[name="title"]', 'content', fullTitle, 'title');
        
        if (keywords) {
            updateMeta('meta[name="keywords"]', 'content', keywords, 'keywords');
        }

        if (noIndex) {
            updateMeta('meta[name="robots"]', 'content', 'noindex, nofollow', 'robots');
        } else {
            updateMeta('meta[name="robots"]', 'content', 'index, follow', 'robots');
        }

        // Canonical URL
        updateLink('canonical', fullUrl);

        // Hreflang
        updateLink('alternate', `${BASE_URL}${canonicalUrl || '/'}`, { hreflang: 'de' });
        updateLink('alternate', `${BASE_URL}${canonicalUrl || '/'}?lang=en`, { hreflang: 'en' });
        updateLink('alternate', `${BASE_URL}${canonicalUrl || '/'}`, { hreflang: 'x-default' });

        // Open Graph Tags
        updateMeta('meta[property="og:title"]', 'content', fullTitle, 'og:title');
        updateMeta('meta[property="og:description"]', 'content', description, 'og:description');
        updateMeta('meta[property="og:type"]', 'content', ogType, 'og:type');
        updateMeta('meta[property="og:url"]', 'content', fullUrl, 'og:url');
        updateMeta('meta[property="og:image"]', 'content', ogImage, 'og:image');
        updateMeta('meta[property="og:image:alt"]', 'content', ogImageAlt, 'og:image:alt');
        updateMeta('meta[property="og:locale"]', 'content', locale, 'og:locale');
        updateMeta('meta[property="og:site_name"]', 'content', 'KoriCode', 'og:site_name');

        // Article-specific Open Graph Tags
        if (ogType === 'article') {
            if (publishedTime) {
                updateMeta(
                    'meta[property="article:published_time"]',
                    'content',
                    publishedTime,
                    'article:published_time'
                );
            }
            if (modifiedTime) {
                updateMeta(
                    'meta[property="article:modified_time"]',
                    'content',
                    modifiedTime,
                    'article:modified_time'
                );
            }
            if (author) {
                updateMeta(
                    'meta[property="article:author"]',
                    'content',
                    author,
                    'article:author'
                );
            }
            if (section) {
                updateMeta(
                    'meta[property="article:section"]',
                    'content',
                    section,
                    'article:section'
                );
            }
        }

        // Twitter Card Tags
        updateMeta('meta[property="twitter:card"]', 'content', twitterCard, 'twitter:card');
        updateMeta('meta[property="twitter:title"]', 'content', fullTitle, 'twitter:title');
        updateMeta(
            'meta[property="twitter:description"]',
            'content',
            description,
            'twitter:description'
        );
        updateMeta('meta[property="twitter:image"]', 'content', ogImage, 'twitter:image');
        updateMeta('meta[property="twitter:url"]', 'content', fullUrl, 'twitter:url');

        // Update HTML lang attribute
        document.documentElement.lang = language;

        // Structured Data
        if (structuredData) {
            const existingScript = document.querySelector(
                'script[data-seo-structured-data]'
            );
            if (existingScript) {
                existingScript.remove();
            }

            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.setAttribute('data-seo-structured-data', 'true');
            script.textContent = JSON.stringify(structuredData);
            document.head.appendChild(script);
        }

        // Cleanup function
        return () => {
            const dynamicScript = document.querySelector(
                'script[data-seo-structured-data]'
            );
            if (dynamicScript) {
                dynamicScript.remove();
            }
        };
    }, [
        fullTitle,
        description,
        keywords,
        fullUrl,
        ogType,
        ogImage,
        ogImageAlt,
        twitterCard,
        noIndex,
        language,
        locale,
        publishedTime,
        modifiedTime,
        author,
        section,
        structuredData,
    ]);

    // This component doesn't render anything visible
    return null;
}

/**
 * Generiert strukturierte Daten für Breadcrumbs
 */
export function generateBreadcrumbSchema(
    items: Array<{ name: string; url: string }>
): object {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: `${BASE_URL}${item.url}`,
        })),
    };
}

/**
 * Generiert strukturierte Daten für Blog-Artikel
 */
export function generateArticleSchema(article: {
    title: string;
    description: string;
    slug: string;
    image?: string;
    publishedDate: string;
    modifiedDate?: string;
    author?: string;
    tags?: string[];
}): object {
    return {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        '@id': `${BASE_URL}/blog/${article.slug}`,
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${BASE_URL}/blog/${article.slug}`,
        },
        headline: article.title,
        description: article.description,
        image: article.image || DEFAULT_IMAGE,
        datePublished: article.publishedDate,
        dateModified: article.modifiedDate || article.publishedDate,
        author: {
            '@type': 'Person',
            name: article.author || 'KoriCode',
            url: BASE_URL,
        },
        publisher: {
            '@type': 'Organization',
            name: 'KoriCode',
            logo: {
                '@type': 'ImageObject',
                url: `${BASE_URL}/favicon-512.png`,
            },
        },
        keywords: article.tags?.join(', '),
        inLanguage: 'de-DE',
    };
}

/**
 * Generiert strukturierte Daten für FAQ-Seiten
 */
export function generateFAQSchema(
    faqs: Array<{ question: string; answer: string }>
): object {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
            },
        })),
    };
}

export default SEO;
